// src/pages/AdminNotifications.tsx
import React, { useState, useEffect, useCallback } from 'react'
import { apiService } from '../services/api'
import toast from 'react-hot-toast'
import { Send, Loader2, RefreshCw, Trash2, Eye, X, Power } from 'lucide-react'

const PRIORITIES = [
  { value: 'info', label: 'Bilgi', cls: 'bg-blue-100 text-blue-700' },
  { value: 'warning', label: 'Uyarı', cls: 'bg-orange-100 text-orange-700' },
  { value: 'important', label: 'Önemli', cls: 'bg-red-100 text-red-700' },
]
const FREQUENCIES = [
  { value: 'once', label: 'Tek seferlik' },
  { value: 'daily', label: 'Günlük' },
  { value: 'weekly', label: 'Haftalık' },
  { value: 'monthly', label: 'Aylık' },
]
const priorityLabel = (v: string) => PRIORITIES.find(p => p.value === v)?.label || v
const priorityCls = (v: string) => PRIORITIES.find(p => p.value === v)?.cls || 'bg-gray-100 text-gray-700'
const freqLabel = (v: string) => FREQUENCIES.find(f => f.value === v)?.label || v

interface SelUser { id: string; fullName: string; email: string }

export default function AdminNotifications() {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [priority, setPriority] = useState('info')
  const [frequency, setFrequency] = useState('once')
  const [isForAll, setIsForAll] = useState(true)
  const [selectedUsers, setSelectedUsers] = useState<SelUser[]>([])
  const [userSearch, setUserSearch] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [sending, setSending] = useState(false)

  const [list, setList] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const [recipModal, setRecipModal] = useState<{ id: string; title: string } | null>(null)
  const [recipData, setRecipData] = useState<{ read: any[]; unread: any[] } | null>(null)
  const [recipLoading, setRecipLoading] = useState(false)

  const loadList = useCallback(async () => {
    setLoading(true)
    try { setList(await apiService.getAdminNotifications()) }
    catch { toast.error('Bildirimler yüklenemedi') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadList() }, [loadList])

  // Kullanıcı arama (debounce)
  useEffect(() => {
    if (isForAll || userSearch.trim().length < 2) { setSearchResults([]); return }
    const t = setTimeout(async () => {
      try {
        const res = await apiService.getAdminUsers(1, 10, userSearch.trim())
        setSearchResults(res.users || [])
      } catch { setSearchResults([]) }
    }, 400)
    return () => clearTimeout(t)
  }, [userSearch, isForAll])

  const addUser = (u: any) => {
    if (!selectedUsers.find(s => s.id === u.id)) setSelectedUsers(prev => [...prev, { id: u.id, fullName: u.fullName, email: u.email }])
    setUserSearch(''); setSearchResults([])
  }
  const removeUser = (id: string) => setSelectedUsers(prev => prev.filter(s => s.id !== id))

  const send = async () => {
    if (!title.trim() || !message.trim()) { toast.error('Başlık ve mesaj zorunlu'); return }
    if (!isForAll && selectedUsers.length === 0) { toast.error('Kullanıcı seç'); return }
    setSending(true)
    try {
      await apiService.createNotification({
        title: title.trim(), message: message.trim(), priority, frequency, isForAll,
        userIds: isForAll ? undefined : selectedUsers.map(u => u.id),
      })
      toast.success('Bildirim gönderildi')
      setTitle(''); setMessage(''); setPriority('info'); setFrequency('once'); setIsForAll(true); setSelectedUsers([])
      loadList()
    } catch (e: any) { toast.error(e.message || 'Gönderilemedi') }
    finally { setSending(false) }
  }

  const openRecipients = async (n: any) => {
    setRecipModal({ id: n.id, title: n.title }); setRecipData(null); setRecipLoading(true)
    try { setRecipData(await apiService.getNotificationRecipients(n.id)) }
    catch { toast.error('Yüklenemedi') }
    finally { setRecipLoading(false) }
  }

  const resend = async (n: any) => {
    if (!window.confirm('Okumayanlara aynı mesaj tekrar gönderilsin mi?')) return
    try {
      const res = await apiService.resendNotificationUnread(n.id)
      toast.success(res.resent > 0 ? `${res.resent} kişiye tekrar gönderildi` : 'Okumayan kalmamış')
      loadList()
    } catch { toast.error('Gönderilemedi') }
  }

  const toggleActive = async (n: any) => {
    try { await apiService.toggleNotificationActive(n.id); loadList() }
    catch { toast.error('Güncellenemedi') }
  }

  const del = async (n: any) => {
    if (!window.confirm('Bu bildirim silinsin mi?')) return
    try { await apiService.deleteNotification(n.id); toast.success('Silindi'); loadList() }
    catch { toast.error('Silinemedi') }
  }

  const fmt = (d: string) => new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="space-y-6">
      {/* Yeni bildirim formu */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <h3 className="font-bold text-gray-800">Yeni Bildirim</h3>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Başlık"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Mesaj" rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Öncelik</label>
            <select value={priority} onChange={e => setPriority(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Sıklık</label>
            <select value={frequency} onChange={e => setFrequency(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Hedef</label>
            <select value={isForAll ? 'all' : 'some'} onChange={e => setIsForAll(e.target.value === 'all')} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="all">Herkes</option>
              <option value="some">Belirli kullanıcı(lar)</option>
            </select>
          </div>
        </div>

        {!isForAll && (
          <div>
            <div className="relative">
              <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Kullanıcı ara (isim/e-posta)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              {searchResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow max-h-48 overflow-y-auto">
                  {searchResults.map(u => (
                    <button key={u.id} onClick={() => addUser(u)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm">
                      {u.fullName} <span className="text-gray-400">· {u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedUsers.map(u => (
                  <span key={u.id} className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 text-xs px-2 py-1 rounded-full">
                    {u.fullName}
                    <button onClick={() => removeUser(u.id)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        <button onClick={send} disabled={sending}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Gönder
        </button>
      </div>

      {/* Gönderilenler */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h3 className="font-bold text-gray-800">Gönderilenler</h3>
          <button onClick={loadList} className="text-gray-500 hover:text-blue-600"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
        ) : list.length === 0 ? (
          <p className="text-center text-gray-500 py-8 text-sm">Henüz bildirim yok.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {list.map(n => (
              <div key={n.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${priorityCls(n.priority)}`}>{priorityLabel(n.priority)}</span>
                      <span className="text-xs text-gray-500">{freqLabel(n.frequency)}</span>
                      <span className="text-xs text-gray-500">{n.isForAll ? 'Herkes' : 'Seçili'}</span>
                      {!n.isActive && <span className="text-xs text-gray-400">(kapalı)</span>}
                      <span className="font-semibold text-gray-800">{n.title}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-1">{fmt(n.createdAt)}</p>
                  </div>
                  <button onClick={() => openRecipients(n)} className="text-sm text-purple-600 hover:underline flex items-center gap-1 shrink-0">
                    <Eye className="w-4 h-4" /> {n.readCount}/{n.recipientCount}
                  </button>
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <button onClick={() => resend(n)} className="text-xs text-orange-600 hover:underline flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Okumayanlara tekrar gönder
                  </button>
                  {n.frequency !== 'once' && (
                    <button onClick={() => toggleActive(n)} className="text-xs text-gray-500 hover:underline flex items-center gap-1">
                      <Power className="w-3 h-3" /> {n.isActive ? 'Durdur' : 'Aç'}
                    </button>
                  )}
                  <button onClick={() => del(n)} className="text-xs text-red-500 hover:underline flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Sil
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Okuyan / Okumayan modal */}
      {recipModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800 truncate">{recipModal.title}</h3>
              <button onClick={() => setRecipModal(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto p-4">
              {recipLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
              ) : recipData ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-green-700 mb-2">Okuyanlar ({recipData.read.length})</p>
                    {recipData.read.length === 0 ? <p className="text-xs text-gray-400">—</p> :
                      recipData.read.map((u: any) => <div key={u.id} className="text-sm text-gray-700 py-1">{u.fullName} <span className="text-gray-400">· {u.email}</span></div>)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-red-600 mb-2">Okumayanlar ({recipData.unread.length})</p>
                    {recipData.unread.length === 0 ? <p className="text-xs text-gray-400">—</p> :
                      recipData.unread.map((u: any) => <div key={u.id} className="text-sm text-gray-700 py-1">{u.fullName} <span className="text-gray-400">· {u.email}</span></div>)}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
